import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { LuArrowLeft, LuDownload, LuPencil, LuFileText } from "react-icons/lu";
import CircularProgress from "@mui/material/CircularProgress";
import { useMaterialUIController } from "context";
import { DashboardNavigation } from "layouts/businessUserDashboard";
import AppHeader from "components/AppHeader";
import ActivityLog from "layouts/activityLog/activityLog";
import CreditNoteDetailSkeleton from "./CreditNoteDetailSkeleton";
import { appConstants } from "utils/appConstants";
import { getScreenRight } from "utils/generic";
import "./creditNoteDetail.css";

const displayDate = (value) => {
  if (!value) return "Not recorded";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
};
export default function ViewCredits({ initialRecord = null }) {
  const { id } = useParams();
  const [controller] = useMaterialUIController();
  const [navOpen, setNavOpen] = useState(false);
  const [record, setRecord] = useState(initialRecord);
  const [loading, setLoading] = useState(!initialRecord);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  let user = {};
  try { user = JSON.parse(sessionStorage.getItem("LOGIN_DETAILS") || "{}"); } catch (_) {}
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setRecord(null);
    try {
      const { data } = await axios.post(`${appConstants.baseURL}credit/viewCreditByID`, { creditID: id });
      if (!data.Result_Status || !data.Result_Output?.[0]) throw new Error(data.Result_Message || "Credit note not found.");
      setRecord(data.Result_Output[0]);
    } catch (e) { setError(e.response?.data?.Result_Message || e.message || "Unable to load credit note."); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => {
    if (!initialRecord) load();
  }, [initialRecord, load]);
  useEffect(() => {
    let mounted = true;
    getScreenRight("Credit Notes").then((right) => { if (mounted) setCanEdit(right === "Full Access"); }).catch(() => {});
    return () => { mounted = false; };
  }, []);
  const download = async () => {
    setDownloading(true);
    setDownloadError("");
    try {
      const { data } = await axios.post(`${appConstants.baseURL}invoicePdfFormat/fetchInvoicePdfTheme`, { businessUserID: user.businessUserID || user._id });
      const themeID = data.Result_Output?.[0]?._id;
      if (!themeID) throw new Error("Add a branding theme in settings before downloading.");
      const response = await axios.post(`${appConstants.baseURL}invoicePdfFormat/downloadCredit`, { _id: id, themeID });
      if (!response.data || typeof response.data !== "string") throw new Error("Unable to generate the credit note PDF. Please try again.");
      const bytes = Uint8Array.from(atob(response.data), (character) => character.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${record.creditNumber || "credit-note"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setDownloadError(e.response?.data?.Result_Message || e.message || "Unable to download credit note."); }
    finally { setDownloading(false); }
  };
  const currency = !record?.currencyValue || record.currencyValue === "$" ? "AUD" : record.currencyValue;
  const creditLifecycleStatus = record
    ? record.activityStatus && record.activityStatus !== "Draft"
      ? record.activityStatus
      : Number(record.emailSent) > 0
      ? "Issued"
      : record.creditStatus || record.activityStatus || "Draft"
    : "";
  const money = (amount) => {
    if (amount === undefined || amount === null || amount === "") return "—";
    const number = Number(amount);
    if (!Number.isFinite(number)) return "—";
    try { return new Intl.NumberFormat("en-AU", { style: "currency", currency }).format(number); }
    catch (_) { return `${currency} ${number.toFixed(2)}`; }
  };
  return (
    <div className={`credit-detail-shell${controller.darkMode ? " dark" : ""}`}>
      <DashboardNavigation open={navOpen} onClose={() => setNavOpen(false)} activePath="/creditNote" organisation={user.companyName || user.businessName || "Your business"} role={user.roleName || "Owner"} />
      <div className="credit-detail-main">
        <AppHeader onOpenNav={() => setNavOpen(true)} userName={[user.firstName, user.lastName].filter(Boolean).join(" ")} searchPlaceholder="Search invoices, quotes, contacts" />
        <main className="credit-detail-page">
          {<><nav className="credit-detail-breadcrumb" aria-label="Breadcrumb"><Link to="/creditNote">Credit notes</Link><span>/</span><span>{record?.creditNumber || "View credit note"}</span></nav>
          <header className="credit-detail-heading"><div><Link className="credit-detail-back" to="/creditNote"><LuArrowLeft /> All credit notes</Link><h1>{record?.creditNumber || "Credit note"}</h1><p>Review the credit issued to your customer.</p></div>
            {record && <div className="credit-detail-actions">{canEdit && <Link className="credit-detail-button" to={`/creditNote/edit/${id}`}><LuPencil /> Edit credit note</Link>}<button className="credit-detail-button primary" disabled={downloading} onClick={download}>{downloading ? <CircularProgress size={16} color="inherit" aria-hidden="true" /> : <LuDownload />}{downloading ? "Generating PDF…" : "Download PDF"}</button></div>}
          </header></>}
          {downloadError && <p role="alert" className="credit-detail-error">{downloadError}</p>}
          {error ? <section className="credit-detail-card" role="alert"><h2>Unable to open credit note</h2><p>{error}</p><button className="credit-detail-button" onClick={load}>Try again</button></section> : <>
            {(loading || historyLoading) && <CreditNoteDetailSkeleton legacy showHeading={false} label="Loading credit note details…" />}
            <div className={`credit-detail-loaded${loading || historyLoading ? " is-loading" : ""}`}>
            {record && <>
            <div className="credit-detail-summary">
              <section className="credit-detail-card"><span>Credit total</span><strong>{money(record.finalAmount)}</strong><small>{currency} · GST {record.gstType || "not recorded"}</small></section>
              <section className="credit-detail-card"><span>Customer</span><strong>{record.contactName || "Not recorded"}</strong><small>Credit recipient</small></section>
              <section className="credit-detail-card"><span>Credit note date</span><strong>{displayDate(record.creditDate)}</strong><small>Reference: {record.purchaseOrderNumber || "Not recorded"}</small></section>
            </div>
            <div className="credit-detail-grid">
              <section className="credit-detail-card credit-detail-document"><header><div><LuFileText /><h2>Credit note</h2></div>{creditLifecycleStatus && <span className="credit-detail-badge">{creditLifecycleStatus}</span>}</header>
                <div className="credit-detail-identities"><div><span>Credited to</span><h3>{record.contactName || "Not recorded"}</h3></div><dl><dt>Credit number</dt><dd>{record.creditNumber}</dd><dt>Date</dt><dd>{displayDate(record.creditDate)}</dd><dt>Reference</dt><dd>{record.purchaseOrderNumber || "—"}</dd><dt>Currency</dt><dd>{currency}</dd></dl></div>
                <div className="credit-detail-table-scroll"><table><caption>Credited items · GST {record.gstType || "not recorded"}</caption><thead><tr><th>Item / description</th><th>Account</th><th>Qty</th><th>Unit price</th><th>GST</th><th>Discount</th><th>Line total</th></tr></thead><tbody>{record.itemsList?.length ? record.itemsList.map((item, index) => <tr key={item._id || index}><td><b>{item.itemName || "Item"}</b>{item.itemDescription && <small>{item.itemDescription}</small>}</td><td>{item.categoryCodeName || "—"}</td><td>{item.itemQuantity ?? "—"}</td><td>{money(item.itemPrice)}</td><td>{item.gst === undefined || item.gst === null ? "—" : `${item.gst}%`}</td><td>{money(item.discountAmount ?? 0)}</td><td>{money(item.totalItemPrice)}</td></tr>) : <tr><td colSpan={7}>No items recorded.</td></tr>}</tbody></table></div>
                <div className="credit-detail-totals"><dl><dt>Total amount</dt><dd>{money(record.totalAmount)}</dd><dt>Discount</dt><dd>{money(record.totalDiscountAmount)}</dd>{record.totalGSTAmount !== undefined && <><dt>GST</dt><dd>{money(record.totalGSTAmount)}</dd></>}<dt className="grand">Credit total</dt><dd className="grand">{money(record.finalAmount)}</dd></dl></div>
                {record.paymentInstruction && <section className="credit-detail-notes"><h3>Instructions</h3><p>{record.paymentInstruction}</p></section>}
              </section>
              <aside className="credit-detail-card credit-detail-context"><h2>Credit details</h2>{record.reason && <p>Reason: {record.reason}</p>}<p>This credit note records a credit to the customer. Amounts shown come from the saved credit note.</p><dl><dt>GST treatment</dt><dd>{record.gstType || "Not recorded"}</dd><dt>Credit note date</dt><dd>{displayDate(record.creditDate)}</dd></dl>{record.internalNotes && <section className="credit-detail-notes"><h3>Private notes</h3><p>{record.internalNotes}</p></section>}<Link to="/creditNote">View all credit notes →</Link></aside>
            </div>
            </>}
            <section className="credit-detail-card credit-detail-activity"><ActivityLog key={id} name="Credit Notes" variant="detail" onLoadingChange={setHistoryLoading} /></section>
            </div>
          </>}
        </main>
      </div>
    </div>
  );
}
